import React, { useEffect, useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Bell, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Clock, Info, LayoutGrid, Search, Stethoscope, X,
} from "lucide-react";
import type { ClientRecord, PendingBucketKey, SearchMode } from "../types";
import { SOURCES, BIRTH_SEXES } from "../data/lookups";
import { Field, Select, TextInput, inputCls } from "../components/FormControls";
import { hasHivOrStdData } from "../components/TopBar";

/* ------------------------------------------------------------------ */

export interface DashboardProps {
  records: ClientRecord[];
  onOpenRecord: (rec: ClientRecord) => void;
  registerTourTarget: (key: string) => (el: HTMLElement | null) => void;
}
export type DashboardTabKey = "notifications" | "search" | "lookup";
export interface DashboardTab {
  key: DashboardTabKey;
  label: string;
  icon: LucideIcon;
}
export function Dashboard({ records, onOpenRecord, registerTourTarget }: DashboardProps) {
  const [tab, setTab] = useState<DashboardTabKey>("search");
  const tabs: DashboardTab[] = [
    { key: "notifications", label: "Notifications", icon: Bell },
    { key: "search", label: "Client Search", icon: Search },
    { key: "lookup", label: "Case Registry", icon: LayoutGrid },
  ];

  const newCount = useMemo(() => records.filter((r) => r.recStatus === "New").length, [records]);

  return (
    <div className="p-3 md:p-4">
      <div className="mb-3 flex gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            ref={registerTourTarget(`tab-${t.key}`)}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 border-b-2 px-3.5 py-2 text-sm font-medium transition ${
              tab === t.key ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {t.key === "notifications" && newCount > 0 && (
              <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">{newCount}</span>
            )}
          </button>
        ))}
      </div>
      {tab === "notifications" && <NotificationsTab records={records} onOpenRecord={onOpenRecord} />}
      {tab === "search" && <ClientSearchTab records={records} onOpenRecord={onOpenRecord} />}
      {tab === "lookup" && <LookupDashboardTab records={records} onOpenRecord={onOpenRecord} />}
    </div>
  );
}

/* ---------------------------- Notifications -------------------------- */
/* Full list of newly added HIV/STD records — the same underlying data   */
/* the bell icon dropdown shows, just expanded into a dedicated tab.     */

export interface NotificationsTabProps {
  records: ClientRecord[];
  onOpenRecord: (rec: ClientRecord) => void;
}
export function NotificationsTab({ records, onOpenRecord }: NotificationsTabProps) {
  const newRecords = useMemo(() => records.filter((r) => r.recStatus === "New"), [records]);

  return (
    <div>
      <div className="mb-3">
        <h2 className="text-base font-semibold text-slate-800">New HIV / STD Records</h2>
        <p className="text-xs text-slate-400">Records recently added to the system that may still need review.</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {newRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
            <Bell className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-400">No newly added HIV or STD records.</p>
          </div>
        ) : (
          <ul className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
            {newRecords.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => onOpenRecord(r)}
                  className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition hover:bg-slate-50 md:px-6"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
                    <Stethoscope className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{r.firstName} {r.lastName}</span>
                      {!hasHivOrStdData(r) && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">Pending data</span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">{r.hospital} – {r.facility}</span>
                    <span className="mt-0.5 block text-xs text-slate-400">
                      {r.id} · Created {r.createdDate} by {r.createdBy}
                    </span>
                  </span>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------------------------- Client Search ------------------------ */
/* Fields mirror the legacy "Client Search" screen: Source, First Name*,
   Last Name*, Date of Birth*, Birth Sex, Clear / Search, and a
   "Matching Record" grid with an Address sub-row per result.          */

export interface ClientSearchTabProps {
  records: ClientRecord[];
  onOpenRecord: (rec: ClientRecord) => void;
}
export interface SearchForm {
  source: string;
  firstName: string;
  lastName: string;
  dob: string;
  birthSex: string;
}
export function ClientSearchTab({ records, onOpenRecord }: ClientSearchTabProps) {
  const [mode, setMode] = useState<SearchMode>("exact");
  const [form, setForm] = useState<SearchForm>({ source: "All", firstName: "", lastName: "", dob: "", birthSex: "" });
  const [submitted, setSubmitted] = useState<SearchForm | null>(null);

  function runSearch() {
    setSubmitted({ ...form });
  }
  function clear() {
    setForm({ source: "All", firstName: "", lastName: "", dob: "", birthSex: "" });
    setSubmitted(null);
  }

  const results = useMemo(() => {
    if (!submitted) return [];
    return records.filter((r) => {
      const nameOk = mode === "wildcard"
        ? (!submitted.firstName || r.firstName.toLowerCase().includes(submitted.firstName.toLowerCase())) &&
          (!submitted.lastName || r.lastName.toLowerCase().includes(submitted.lastName.toLowerCase()))
        : (!submitted.firstName || r.firstName.toLowerCase() === submitted.firstName.toLowerCase()) &&
          (!submitted.lastName || r.lastName.toLowerCase() === submitted.lastName.toLowerCase());
      const dobOk = !submitted.dob || r.dob === submitted.dob;
      const sexOk = !submitted.birthSex || r.birthSex === submitted.birthSex;
      const sourceOk = submitted.source === "All" || r.source === submitted.source;
      return nameOk && dobOk && sexOk && sourceOk;
    });
  }, [records, submitted, mode]);

  return (
    <div>
      <div className="mb-3 rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 pt-3">
          <h2 className="mb-2 text-sm font-semibold text-slate-800">Client Search</h2>
          <div className="flex gap-5 text-sm">
            <button
              onClick={() => setMode("exact")}
              className={`border-b-2 pb-2 font-medium ${mode === "exact" ? "border-cyan-600 text-slate-800" : "border-transparent text-slate-400 hover:text-slate-600"}`}
            >
              Client Search
            </button>
            <button
              onClick={() => setMode("wildcard")}
              className={`flex items-center gap-1 border-b-2 pb-2 font-medium ${mode === "wildcard" ? "border-cyan-600 text-slate-800" : "border-transparent text-cyan-600 hover:text-cyan-700"}`}
            >
              Wildcard Search <Info className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter") runSearch(); }}
          className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5"
        >
          <Field label="Source">
            <Select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option>All</option>
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="First Name" required>
            <TextInput value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="First Name" />
          </Field>
          <Field label="Last Name" required>
            <TextInput value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Last Name" />
          </Field>
          <Field label="Date of Birth" required>
            <TextInput type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          </Field>
          <Field label="Birth Sex">
            <Select value={form.birthSex} onChange={(e) => setForm({ ...form, birthSex: e.target.value })}>
              <option value="">Select Birth Sex</option>
              {BIRTH_SEXES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-5 lg:justify-end">
            <button type="button" onClick={clear} className="rounded-full border border-cyan-600 px-5 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50">
              Clear
            </button>
            <button type="button" onClick={runSearch} className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700">
              Search
            </button>
          </div>
        </div>
      </div>

      {submitted && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-slate-800">Matching Record</h3>
          </div>
          <div className="max-h-[360px] overflow-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2">First Name</th>
                  <th className="px-4 py-2">Middle Name</th>
                  <th className="px-4 py-2">Last Name</th>
                  <th className="px-4 py-2">Date of Birth</th>
                  <th className="px-4 py-2">Birth Sex</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-800">{r.firstName}</td>
                      <td className="px-4 py-2 text-slate-500">{r.middleName || "—"}</td>
                      <td className="px-4 py-2 text-slate-700">{r.lastName}</td>
                      <td className="px-4 py-2 tabular-nums text-slate-600">{r.dob}</td>
                      <td className="px-4 py-2 uppercase text-slate-600">{r.birthSex}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => onOpenRecord(r)}
                          className="rounded-full bg-cyan-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <td colSpan={6} className="px-4 py-1.5 text-xs text-slate-500">
                        <span className="font-semibold text-slate-600">Address:</span>{" "}
                        {[r.street1, r.city, r.state, r.zip].filter(Boolean).join(" ") || "No address on file"}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                {results.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">
                      No matching records found for these search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Case Registry ----------------------- */
/* Matches the legacy grid: Hospital/Location, Provider MRN, eHARS #,   */
/* STARS Profile ID, Rec Status, Linkage Status, Created by, Created    */
/* Date, Last Updated by — plus the pending-age legend turned into a    */
/* clickable filter bar underneath the grid.                            */

export function pendingBucket(days: number | null): PendingBucketKey | null {
  if (days == null) return null;
  return days < 14 ? "under14" : "over14";
}

export interface LookupDashboardTabProps {
  records: ClientRecord[];
  onOpenRecord: (rec: ClientRecord) => void;
}
export function LookupDashboardTab({ records, onOpenRecord }: LookupDashboardTabProps) {
  const [keyword, setKeyword] = useState<string>("");
  const [bucketFilter, setBucketFilter] = useState<PendingBucketKey | null>(null);
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const pageSize = 6;

  const filtered = useMemo(() => {
    let rows = records;
    if (keyword.trim()) {
      const k = keyword.toLowerCase();
      rows = rows.filter((r) =>
        [r.hospital, r.facility, r.providerMRN, r.eharsId, r.starsProfileId, r.createdBy]
          .filter(Boolean).some((v) => v.toLowerCase().includes(k))
      );
    }
    if (bucketFilter) {
      rows = rows.filter((r) => r.linkageGridStatus === "Pending" && pendingBucket(r.pendingDays) === bucketFilter);
    }
    return rows;
  }, [records, keyword, bucketFilter]);

  useEffect(() => {
    setPage(0);
  }, [keyword, bucketFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * pageSize;
  const paginated = filtered.slice(pageStart, pageStart + pageSize);

  const under14Count = records.filter((r) => r.linkageGridStatus === "Pending" && pendingBucket(r.pendingDays) === "under14").length;
  const over14Count = records.filter((r) => r.linkageGridStatus === "Pending" && pendingBucket(r.pendingDays) === "over14").length;

  return (
    <div>
      <div className="mb-3 flex flex-nowrap items-center justify-between gap-3 overflow-x-auto pb-1">
        <h2 className="shrink-0 whitespace-nowrap text-base font-semibold text-slate-800">Case Registry</h2>
        <div className="flex flex-nowrap items-center gap-3">
          <div className="relative w-72 shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={keyword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)} placeholder="Search by Keywords" className={inputCls + " pl-9"} />
          </div>
          <div className="flex flex-nowrap items-center gap-2">
            <span className="whitespace-nowrap text-xs font-medium text-slate-400">Filter by:</span>
            <button
              onClick={() => setBucketFilter(bucketFilter === "under14" ? null : "under14")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${
                bucketFilter === "under14" ? "bg-amber-100 text-amber-900 ring-amber-400" : "bg-amber-50 text-amber-800 ring-amber-200 hover:ring-amber-400"
              }`}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" />
              Pending for update under 14 days
              <span className="rounded-full bg-white/70 px-1.5 text-[10px] font-semibold">{under14Count}</span>
            </button>
            <button
              onClick={() => setBucketFilter(bucketFilter === "over14" ? null : "over14")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition ${
                bucketFilter === "over14" ? "bg-rose-100 text-rose-900 ring-rose-400" : "bg-rose-50 text-rose-700 ring-rose-200 hover:ring-rose-400"
              }`}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-rose-400" />
              Pending for update over 14 days
              <span className="rounded-full bg-white/70 px-1.5 text-[10px] font-semibold">{over14Count}</span>
            </button>
            {bucketFilter && (
              <button onClick={() => setBucketFilter(null)} className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-slate-400 hover:text-slate-700">
                <X className="h-3 w-3" /> Clear filter
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Hospital/Location</th>
                <th className="px-4 py-2.5">Provider MRN</th>
                <th className="px-4 py-2.5">eHARS #</th>
                <th className="px-4 py-2.5">STARS Profile ID</th>
                <th className="px-4 py-2.5">Rec Status</th>
                <th className="px-4 py-2.5">Linkage Status</th>
                <th className="px-4 py-2.5">Activity</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => {
                const bucket = pendingBucket(r.pendingDays);
                return (
                  <tr
                    key={r.id}
                    onClick={() => onOpenRecord(r)}
                    className="cursor-pointer border-b border-slate-50 transition last:border-0 hover:bg-cyan-50/40"
                  >
                    <td className="px-4 py-2">
                      <p className="font-medium text-slate-800">{r.hospital}</p>
                      <p className="pl-2 text-xs text-slate-400">- {r.facility}</p>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{r.providerMRN || "-"}</td>
                    <td className="px-4 py-2 text-slate-600">{r.eharsId || "-"}</td>
                    <td className="px-4 py-2 text-slate-600">{r.starsProfileId || "-"}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        r.recStatus === "Updated" ? "bg-sky-50 text-sky-700 ring-sky-600/20" : "bg-slate-100 text-slate-600 ring-slate-500/20"
                      }`}>
                        {r.recStatus}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {r.linkageGridStatus === "Pending" ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          bucket === "under14" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"
                        }`}>
                          Pending
                        </span>
                      ) : (
                        <span className="text-slate-600">{r.linkageGridStatus}</span>
                      )}
                    </td>
                    <td className="relative px-4 py-2" onClick={(e: React.MouseEvent<HTMLTableCellElement>) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenActivityId(openActivityId === r.id ? null : r.id)}
                        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                          openActivityId === r.id
                            ? "border-cyan-600 bg-cyan-50 text-cyan-700"
                            : "border-slate-200 text-slate-600 hover:border-cyan-600 hover:text-cyan-700"
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Activity
                      </button>
                      {openActivityId === r.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenActivityId(null)} />
                          <div className="absolute right-4 top-full z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-lg">
                            <dl className="space-y-2">
                              <div className="flex items-center justify-between gap-3">
                                <dt className="text-slate-400">Created by</dt>
                                <dd className="font-medium text-slate-700">{r.createdBy || "-"}</dd>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <dt className="text-slate-400">Created Date</dt>
                                <dd className="font-medium tabular-nums text-slate-700">{r.createdDate || "-"}</dd>
                              </div>
                              <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2">
                                <dt className="text-slate-400">Last Updated by</dt>
                                <dd className="font-medium text-slate-700">{r.lastUpdatedBy || "-"}</dd>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <dt className="text-slate-400">Last Updated Date</dt>
                                <dd className="font-medium tabular-nums text-slate-700">{r.lastUpdatedDate || "-"}</dd>
                              </div>
                            </dl>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">No records match this filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400">
            Rows per page: {pageSize} &nbsp;·&nbsp; {filtered.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1 text-slate-400">
            <button
              onClick={() => setPage(0)}
              disabled={currentPage === 0}
              className="rounded p-1 hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="rounded p-1 hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-1 text-xs tabular-nums text-slate-400">
              {currentPage + 1} / {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={currentPage >= pageCount - 1}
              className="rounded p-1 hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(pageCount - 1)}
              disabled={currentPage >= pageCount - 1}
              className="rounded p-1 hover:bg-slate-100 hover:text-slate-600 disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

